import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Trophy,
  Users,
  Target,
  Globe2,
} from "lucide-react";
import { useFirestoreDoc } from "@/hooks/useFirestoreDoc";
import { useFirestoreCollection } from "@/hooks/useFirestoreCollection";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import Bracket from "@/components/Bracket";
import LiveVoting from "@/components/LiveVoting";
import Ranking from "@/components/Ranking";
import ConnectionError from "@/components/ConnectionError";
import {
  formatDate,
  formatTime,
  toArray,
  normalizeRound,
  ROUND_ORDER,
} from "@/lib/format";

export default function EventDetail() {
  const { id } = useParams();
  const [retryKey, setRetryKey] = useState(0);

  const {
    data: event,
    loading: eventLoading,
    error: eventError,
  } = useFirestoreDoc("eventos", id);

  // Filtered queries by eventId (massively faster than fetching entire collection)
  const { data: torneos, error: torneoError } = useFirestoreCollection(
    "torneos",
    { key: retryKey, where: [["eventId", "==", id]] }
  );
  const { data: votesByEvent, error: votesError } = useFirestoreCollection(
    "votes",
    { key: retryKey, where: [["eventId", "==", id]] }
  );

  // One torneo doc per round (newest wins if a round was regenerated)
  const roundDocs = useMemo(() => {
    const sorted = [...(torneos || [])].sort(
      (a, b) => (b.fechaCreacion?.seconds || 0) - (a.fechaCreacion?.seconds || 0)
    );
    const byRound = {};
    sorted.forEach((t) => {
      const r = normalizeRound(t.ronda) || "Filtros";
      if (!byRound[r]) byRound[r] = t;
    });
    return ROUND_ORDER.filter((r) => byRound[r]).map((r) => byRound[r]);
  }, [torneos]);

  const torneo = roundDocs[roundDocs.length - 1] || null;

  // All MCs that ever took part (including eliminated ones)
  const allNames = useMemo(() => {
    const set = new Set();
    roundDocs.forEach((t) => {
      toArray(t.listaNombres).forEach((n) =>
        set.add(typeof n === "string" ? n : n?.nombre || JSON.stringify(n))
      );
    });
    return Array.from(set);
  }, [roundDocs]);

  const eventVotes = votesByEvent || [];

  const error = eventError || torneoError || votesError;

  if (eventLoading) {
    return (
      <div
        data-testid="event-loading"
        className="mx-auto max-w-7xl px-4 py-16 md:px-8"
      >
        <div className="h-10 w-80 animate-pulse bg-[#0F172A]" />
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse bg-[#0F172A]" />
          ))}
        </div>
        <div className="mt-10 h-96 animate-pulse bg-[#0F172A]" />
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
        <ConnectionError
          error={error}
          onRetry={() => setRetryKey((k) => k + 1)}
        />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 md:px-8">
        <div className="border border-[#1E293B] bg-[#0F172A] p-10 text-center">
          <h2 className="font-display text-3xl tracking-wider text-white">
            Evento no encontrado
          </h2>
          <p className="mt-3 font-body text-sm text-[#94A3B8]">
            El evento solicitado no existe o ha sido eliminado.
          </p>
        </div>
      </div>
    );
  }

  const isLive = !event.isClosed;
  const competitors =
    allNames.length > 0
      ? allNames
      : toArray(event.listaNombres || event.participantes);
  const participantes = competitors.length;
  const currentRound = normalizeRound(torneo?.ronda);

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="flex flex-col gap-2 border border-[#1E293B] bg-[#0F172A] p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-[#FF5500]" />
        <span className="font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
          {label}
        </span>
      </div>
      <span className="font-display text-2xl tracking-wider text-white">
        {value || "—"}
      </span>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-14">
      {/* Header */}
      <section className="mb-10 flex flex-col gap-5 border-b border-[#1E293B] pb-10">
        <div className="flex flex-wrap items-center gap-3">
          {isLive ? (
            <span
              data-testid="event-detail-live"
              className="inline-flex items-center gap-1.5 bg-[#FF5500] px-3 py-1.5 font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-white"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white live-pulse" />
              En vivo
            </span>
          ) : (
            <span className="inline-flex border border-[#1E293B] bg-[#080D1A] px-3 py-1.5 font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
              Finalizado
            </span>
          )}
          {event.modalidad && (
            <span className="font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
              Modalidad · {event.modalidad}
            </span>
          )}
          {currentRound && isLive && (
            <span className="font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF5500]">
              Ronda · {currentRound}
            </span>
          )}
        </div>
        <h1
          data-testid="event-title"
          className="font-display text-5xl leading-none tracking-wider text-white sm:text-6xl lg:text-7xl"
        >
          {event.titulo}
        </h1>
        {event.winner && (
          <div className="flex items-center gap-3">
            <Trophy className="h-5 w-5 text-[#FF5500]" />
            <span className="font-ui text-xs font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
              Campeón:
            </span>
            <span className="font-display text-2xl tracking-wider text-white">
              {event.winner}
            </span>
          </div>
        )}
      </section>

      {/* Stats */}
      <section className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Calendar} label="Fecha" value={formatDate(event.fecha)} />
        <Stat icon={Clock} label="Hora" value={formatTime(event.hora)} />
        <Stat
          icon={MapPin}
          label="Ciudad"
          value={event.city || event.ciudad || event.lugar}
        />
        {(event.country || event.pais) && (
          <Stat
            icon={Globe2}
            label="País"
            value={event.country || event.pais}
          />
        )}
        <Stat
          icon={Users}
          label="Participantes"
          value={participantes || competitors.length}
        />
        {event.maxPointLimit && (
          <Stat
            icon={Target}
            label="Puntaje máx."
            value={event.maxPointLimit}
          />
        )}
        {event.organizaciones && toArray(event.organizaciones).length > 0 && (
          <div className="col-span-2 flex flex-col gap-2 border border-[#1E293B] bg-[#0F172A] p-5 lg:col-span-2">
            <span className="font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
              Organizaciones
            </span>
            <div className="flex flex-wrap gap-2">
              {toArray(event.organizaciones).map((o, i) => (
                <span
                  key={i}
                  className="border border-[#1E293B] bg-[#080D1A] px-2.5 py-1 font-body text-xs text-white"
                >
                  {typeof o === "string" ? o : o.nombre || JSON.stringify(o)}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Tabs */}
      <Tabs defaultValue="bracket" className="w-full">
        <TabsList
          data-testid="detail-tabs"
          className="h-auto border border-[#1E293B] bg-[#0F172A] p-1"
        >
          <TabsTrigger
            value="bracket"
            data-testid="tab-bracket"
            className="px-5 py-2.5 font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#94A3B8] data-[state=active]:bg-[#FF5500] data-[state=active]:text-white"
          >
            Bracket
          </TabsTrigger>
          <TabsTrigger
            value="votes"
            data-testid="tab-votes"
            className="px-5 py-2.5 font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#94A3B8] data-[state=active]:bg-[#FF5500] data-[state=active]:text-white"
          >
            Votos en vivo
          </TabsTrigger>
          <TabsTrigger
            value="ranking"
            data-testid="tab-ranking"
            className="px-5 py-2.5 font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#94A3B8] data-[state=active]:bg-[#FF5500] data-[state=active]:text-white"
          >
            Ranking
          </TabsTrigger>
          <TabsTrigger
            value="competidores"
            data-testid="tab-competidores"
            className="px-5 py-2.5 font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#94A3B8] data-[state=active]:bg-[#FF5500] data-[state=active]:text-white"
          >
            Competidores
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bracket" className="mt-6">
          <Bracket torneo={torneo} torneos={roundDocs} votes={eventVotes} event={event} />
        </TabsContent>

        <TabsContent value="votes" className="mt-6">
          <LiveVoting votes={eventVotes} event={event} torneo={torneo} />
        </TabsContent>

        <TabsContent value="ranking" className="mt-6">
          <Ranking votes={eventVotes} event={event} />
        </TabsContent>

        <TabsContent value="competidores" className="mt-6">
          <div
            data-testid="competidores-grid"
            className="border border-[#1E293B] bg-[#0F172A] p-6"
          >
            <h3 className="mb-5 font-display text-2xl tracking-wider text-white">
              Lista de MCs
            </h3>
            {competitors.length === 0 ? (
              <p className="font-ui text-xs uppercase tracking-[0.25em] text-[#94A3B8]">
                Lista no disponible
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {competitors.map((name, i) => (
                  <div
                    key={i}
                    data-testid={`competitor-${i}`}
                    className="flex items-center gap-3 border border-[#1E293B] bg-[#080D1A] px-4 py-3"
                  >
                    <span className="font-display text-xl tracking-wider text-[#FF5500]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-body text-sm text-white">
                      {typeof name === "string"
                        ? name
                        : name?.nombre || JSON.stringify(name)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
